import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getWithAuth, postWithAuth } from '@/utils/apiClient';
import { toast } from 'react-toastify';
import { logger } from "../../../utils/logger";

/**
 * Hook to manage guided workflow state (pending notes or today's appointments)
 * Handles fetching workflow data and tracking deferred encounters
 */
export function useGuidedWorkflow() {
  const [guidedMode, setGuidedMode] = useState(null);
  const [guidedWorkflowData, setGuidedWorkflowData] = useState(null);
  const [deferredEncounters, setDeferredEncounters] = useState(new Set());
  const location = useLocation();
  // Track the originally requested mode so we know when a fallback happened
  const requestedModeRef = useRef<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    if (mode === 'guided_pending' || mode === 'guided_today') {
      requestedModeRef.current = mode;
      setGuidedMode(mode);
      fetchGuidedWorkflowData(mode);
    } else if (!guidedMode) {
      checkForActiveWorkflow();
    }
  }, [location.search]);

  const checkForActiveWorkflow = async () => {
    try {
      const response = await getWithAuth('/api/guided-notes/workflow/active');
      if (!response.ok) return;
      const data = await response.json();
      const workflow = data?.workflow;
      if (!workflow) return;

      const mode = workflow.mode as 'guided_pending' | 'guided_today';
      requestedModeRef.current = mode;
      setGuidedMode(mode);

      // Transform flat encounters into grouped notes format for PendingNotesBriefing
      const encounters = workflow.encounters || [];
      const patientMap: Record<string, { patient_id: string; patient_name: string; sessions: typeof encounters }> = {};
      for (const enc of encounters) {
        const pid = enc.patient_id;
        if (!patientMap[pid]) {
          patientMap[pid] = { patient_id: pid, patient_name: enc.patient_name, sessions: [] };
        }
        patientMap[pid].sessions.push({
          encounter_id: enc.encounter_id,
          date: enc.date,
          time: enc.time,
          session_type: enc.session_type,
          status: enc.status || 'pending',
          default_template_id: enc.default_template_id,
        });
      }
    const notes = Object.values(patientMap);

      setGuidedWorkflowData({
        notes,
        encounters,
        total_count: workflow.total_count,
        current_index: workflow.current_index,
        completed_count: workflow.completed_count,
        _mode: mode,
        _resumedFromDb: true,
        workflow_id: workflow.id,
      });
      logger.log(
        `Resumed ${mode} workflow from DB: ${workflow.completed_count}/${workflow.total_count} completed`,
      );
    } catch (error) {
      logger.error('Error checking for active workflow:', error);
    }
  };

  const fetchGuidedWorkflowData = async (mode) => {
    try {
      const endpoint = mode === 'guided_pending' 
        ? '/api/guided-notes/pending'
        : '/api/guided-notes/today';
      
      const response = await getWithAuth(endpoint);
      if (response.ok) {
        const data = await response.json();
        const totalCount = (data.total_count as number) || 0;

        // Catch Up should work through pending notes, while today's appointments
        // remain available for the guided greeting and context.
        if (mode === 'guided_today') {
          try {
            const pendingResponse = await getWithAuth('/api/guided-notes/pending');
            if (pendingResponse.ok) {
              const pendingData = await pendingResponse.json();
              const pendingCount = (pendingData.total_count as number) || 0;
              if (pendingCount > 0) {
                logger.log(`📋 Found ${pendingCount} pending notes for Catch Up`);
                setGuidedWorkflowData({
                  ...data,
                  ...pendingData,
                  appointments: data.appointments,
                  _mode: mode,
                  _fromToday: true,
                });
                return;
              }
            }
          } catch (pendingError) {
            logger.error('Error fetching pending notes fallback:', pendingError);
          }
          // If pending also has 0 or failed, keep original data so totalCount === 0 path runs
        }

        setGuidedWorkflowData(data);
        
        if (data && (data.notes?.length > 0 || data.appointments?.length > 0)) {
          setGuidedWorkflowData({ ...data, _mode: mode });
        }
      }
    } catch (error) {
      logger.error('Error fetching guided workflow data:', error);
      toast.error('Failed to load guided workflow data');
    }
  };

  const toggleDeferEncounter = (encounterId) => {
    setDeferredEncounters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(encounterId)) {
        newSet.delete(encounterId);
      } else {
        newSet.add(encounterId);
      }
      return newSet;
    });
  };

  const [isSavingProgress, setIsSavingProgress] = useState(false);

  const saveProgress = useCallback(async () => {
    setIsSavingProgress(true);
    try {
      const response = await postWithAuth('/api/guided-notes/workflow/save-progress', {});
      if (response.ok) {
        toast.success('Progress saved! You can resume anytime.');
      } else {
        toast.error('Could not save progress — no active workflow found.');
      }
    } catch (error) {
      logger.error('Error saving workflow progress:', error);
      toast.error('Failed to save progress');
    } finally {
      setIsSavingProgress(false);
    }
  }, []);

  return {
    guidedMode,
    guidedWorkflowData,
    deferredEncounters,
    toggleDeferEncounter,
    fetchGuidedWorkflowData,
    saveProgress,
    isSavingProgress,
  };
}
